import { Link, Navigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { authReturnLocation } from '../../auth/authReturn';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { LoadingState } from '../../components/ui';

import { readDiscoverTab } from './discoverModel';
import { EXPLORE_IA_SECONDARY } from './exploreIaNav';
import { clearJourneySearchParams } from './journeySearchUrl';
import ForYouPanel from './ForYouPanel';
import SocialExplorePanel from './SocialExplorePanel';
import FollowingExplorePanel from '../social-following/FollowingExplorePanel';

import '../social-compose/social-compose.css';
import './explore.css';

function buildTabHref(targetTab, currentSearchParams) {
  let params = new URLSearchParams(currentSearchParams);
  const currentTab = readDiscoverTab(currentSearchParams);
  params.set('tab', targetTab);
  if (targetTab === 'map') {
    params.set('view', 'map');
  } else {
    params.delete('view');
  }
  if (targetTab !== 'journeys') {
    params = clearJourneySearchParams(params);
  } else if (currentTab !== 'journeys') {
    params.delete('locationId');
  }
  if (targetTab !== 'map' && targetTab !== 'social') {
    params.delete('locationId');
  }
  return `/explore?${params.toString()}`;
}

export default function ExplorePage() {
  const { status, isAuthenticated } = useAuth();
  const { t } = useTravelTranslation();
  const [searchParams] = useSearchParams();
  const tab = readDiscoverTab(searchParams);
  if (tab === 'journeys') {
    const params = new URLSearchParams(searchParams);
    params.delete('tab');
    const query = params.toString();
    return <Navigate replace to={`/discover/journeys${query ? `?${query}` : ''}`} />;
  }


  if (status === 'unknown') {
    return (
      <section className="explore-state" aria-live="polite">
        <LoadingState label={t('travel.discover.loading')} />
      </section>
    );
  }

  return (
    <main
      className={`explore-layout${tab === 'map' ? ' explore-layout--map' : ''}`}
      data-testid="discover-shell"
      data-explore-tab={tab}
    >
      <section className="explore-feed" aria-labelledby="discover-title">
        <div className="explore-intro">
          <p className="explore-intro__eyebrow">{t('travel.common.travel')}</p>
          <div className="explore-intro__row">
            <h1 id="discover-title">{t('travel.discover.title')}</h1>
            {isAuthenticated ? (
              <Link className="explore-compose-cta" to="/social/posts/new">{t('travel.discover.compose')}</Link>
            ) : (
              <Link
                className="explore-compose-cta explore-compose-cta--guest"
                to="/login"
                state={{ from: authReturnLocation({ pathname: '/explore', search: searchParams.toString() ? `?${searchParams.toString()}` : '' }) }}
                data-testid="explore-guest-sign-in"
              >
                {t('travel.nav.signIn')}
              </Link>
            )}
          </div>
          <span>{t('travel.discover.subtitle')}</span>
          {!isAuthenticated ? (
            <div className="explore-first-use" data-testid="explore-first-use-hint">
              <p className="explore-first-use__lead">{t('travel.discover.firstUse.guestLead')}</p>
              <ul className="explore-first-use__list">
                <li>{t('travel.discover.firstUse.guestPost')}</li>
                <li>{t('travel.discover.firstUse.guestJourney')}</li>
                <li>{t('travel.discover.firstUse.guestLogin')}</li>
              </ul>
              <div className="explore-first-use__links">
                <Link to="/public/group-trips" data-testid="explore-first-use-group-trips">
                  {t('travel.nav.findGroupTrips')}
                </Link>
              </div>
            </div>
          ) : null}
        </div>
        <nav
          className="discover-tabs discover-tabs--ia-v2"
          aria-label={t('travel.discover.tabsLabel')}
          data-testid="explore-ia-secondary-nav"
        >
          {EXPLORE_IA_SECONDARY.map((item) => {
            const active = tab === item.tab;
            return (
              <Link
                key={item.id}
                className={`discover-tab${active ? ' discover-tab--active' : ''}`}
                to={buildTabHref(item.tab, searchParams)}
                aria-current={active ? 'page' : undefined}
                data-testid={item.testId}
              >
                {t(item.labelKey)}
              </Link>
            );
          })}
        </nav>
        {tab === 'for-you' ? <ForYouPanel /> : null}
        {tab === 'following' ? <FollowingExplorePanel /> : null}
        {tab === 'map' ? (
          <SocialExplorePanel returnTo="/explore?tab=map" forceView="map" mapMode />
        ) : null}
        {tab === 'social' ? <SocialExplorePanel returnTo="/explore?tab=social" /> : null}
      </section>
      {tab !== 'map' ? (
        <aside className="explore-rail" aria-label={t('travel.discover.railLabel')}>
          <strong>{t('travel.discover.railTitle')}</strong>
          <p>{t('travel.discover.railHint')}</p>
          <nav className="explore-rail__links" aria-label={t('travel.discover.railLabel')}>
            <Link to="/public/group-trips" data-testid="explore-rail-group-trips">
              {t('travel.nav.findGroupTrips')}
            </Link>
          </nav>
        </aside>
      ) : null}
    </main>
  );
}
